import { Button, Stack, Typography, useTheme } from "@mui/material";
import { CheckCircle2 } from "lucide-react";
import type { FC } from "react";
import { RealizationStepRow } from "web_myplann/Goals/Components/RealizationStepRow";
import { useRealizationContent } from "web_myplann/Goals/Hooks/useRealizationContent";
import type { GoalCategoryTypeEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type Props = {
	category: GoalCategoryTypeEnum;
	submitLabel: string;
	cancelLabel: string;
	submitDisabled: boolean;
	isSubmitting: boolean;
	onCancel: () => void;
};

export const RealizationBox: FC<Props> = ({
	category,
	submitLabel,
	cancelLabel,
	submitDisabled,
	isSubmitting,
	onCancel,
}) => {
	const theme = useTheme();
	const { t } = useResource();
	const content = useRealizationContent(category);
	const accent = theme.palette.realization.main;

	return (
		<Stack
			flex="1 0 auto"
			mt={1.2}
			px={2}
			pt={2}
			pb={theme.bottomSpacing.nativeBottomBar}
			sx={{ background: theme.gradients.realization }}
		>
			<Stack
				direction="row"
				alignItems="center"
				gap={0.6}
				alignSelf="flex-start"
				px={1}
				py={0.4}
				mb={1.2}
				borderRadius={999}
				bgcolor={theme.hero.pillBg}
			>
				<CheckCircle2 size={12} color={accent} />
				<Typography variant="overline" color={theme.palette.grey[700]}>
					{content.kicker}
				</Typography>
			</Stack>

			<Typography variant="h2">
				{t(Resources.Goals.Form.Realization.defaultTitle)}
			</Typography>
			<Typography
				variant="caption"
				color={theme.palette.grey[600]}
				mt={0.6}
				lineHeight={1.375}
			>
				{content.description}
			</Typography>

			<Stack mt={2} gap={1.25}>
				{content.steps.map((step) => (
					<RealizationStepRow key={step.title} step={step} accent={accent} />
				))}
			</Stack>

			<Typography
				variant="caption"
				color={theme.palette.grey[600]}
				textAlign="center"
				mt={2}
				pt={2}
				borderTop={theme.colors.border.solid(theme.hero.divider)}
				lineHeight={1.375}
			>
				{t(Resources.Goals.Form.Realization.caption)}
			</Typography>

			<Stack mt={0.8} gap={0.4}>
				<Button
					type="submit"
					variant="primary"
					color="success"
					disabled={submitDisabled}
					loading={isSubmitting}
				>
					{submitLabel}
				</Button>
				<Button
					variant="textLink"
					color="neutral"
					onClick={onCancel}
					sx={{ width: "100%", justifyContent: "center", py: 0.8 }}
				>
					{cancelLabel}
				</Button>
			</Stack>
		</Stack>
	);
};
